import { Lock } from 'lucide-react';
import type { FeatureKey } from '@condo/contracts';
import { Switch } from '@/components/ui/switch';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils/cn';
import { SUBFEATURE_TO_MODULE } from '@condo/contracts';
import {
  FLAG_LABELS,
  FLAG_DESCRIPTIONS,
  FLAG_ICONS,
} from './constants.js';
import type { FlagForm } from './use-flag-form.js';

interface Props {
  flag: FeatureKey;
  form: FlagForm;
}

export function FlagRow({ flag, form }: Props) {
  const Icon = FLAG_ICONS[flag];
  const locked = form.isParentOff(flag);
  const [group, name] = flag.split('.') as [keyof typeof form.staged, string];
  const stagedValue = Boolean(
    (form.staged[group] as Record<string, boolean>)[name],
  );
  const isModified = form.diff.some((d) => d.key === flag);
  const parentModule = SUBFEATURE_TO_MODULE[flag];

  return (
    <div
      className={cn(
        'flex items-start gap-3 border-b border-[var(--color-hairline)] px-2 py-3 last:border-b-0',
        locked && 'opacity-50',
      )}
    >
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-[var(--color-ink-muted)]" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          {isModified && (
            <span
              data-testid={`flag-modified-${flag}`}
              aria-label="modificado"
              className="h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--color-accent)]"
            />
          )}
          <span className="text-sm font-medium">{FLAG_LABELS[flag]}</span>
          {locked && parentModule && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span
                  aria-label="bloqueado"
                  className="text-[var(--color-ink-muted)]"
                >
                  <Lock className="h-3 w-3" />
                </span>
              </TooltipTrigger>
              <TooltipContent>
                Requiere activar {FLAG_LABELS[`modules.${parentModule}` as FeatureKey]} primero
              </TooltipContent>
            </Tooltip>
          )}
        </div>
        <p className="mt-0.5 text-xs text-[var(--color-ink-muted)]">
          {FLAG_DESCRIPTIONS[flag]}
        </p>
      </div>
      <Switch
        aria-label={FLAG_LABELS[flag]}
        checked={stagedValue}
        disabled={locked}
        onCheckedChange={(v) => form.stage(flag, v)}
      />
    </div>
  );
}
